import { MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationRoles } from "../../../../../../../hooks/useApplicationRoles"
import { useApplicationPermissionFirmwareConstraintRoles } from "../../../../../../../hooks/useApplicationPermissionFirmwareConstraintRoles"
import { digitalManagerApi } from "../../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../../components/FormPage"

const validationSchema = z.object({
  applicationRoleId: z.string().min(1, "Role is required"),
})

export function ApplicationPermissionFirmwareConstraintRoleCreate() {
  const { applicationId, permissionId, firmwareConstraintId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}/permissions/${permissionId}/firmware-constraints/${firmwareConstraintId}#roles`

  const { applicationRoles, isLoadingApplicationRoles } = useApplicationRoles(applicationId)
  const { firmwareConstraintRoles, isLoadingFirmwareConstraintRoles } =
    useApplicationPermissionFirmwareConstraintRoles(applicationId, permissionId, firmwareConstraintId)

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<{ applicationRoleId: string }>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({ applicationRoleId: "" })
  }, [reset])

  const onSubmit = (values: { applicationRoleId: string }) => digitalManagerApi
      .post(
        `/applications/${applicationId}/permissions/${permissionId}/firmwareConstraints/${firmwareConstraintId}/roles`,
        { applicationRoleId: values.applicationRoleId }
      )
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Role added to firmware constraint")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add role to firmware constraint")
      })

  const handleRoleSelectChange = useCallback(
    (field: ControllerRenderProps<{ applicationRoleId: string }, "applicationRoleId">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderRoleSelect = useCallback(
    (renderProps: {
      field: ControllerRenderProps<{ applicationRoleId: string }, "applicationRoleId">
    }) => (
      <MUISelect
        name={renderProps.field.name}
        id="applicationRoleId"
        size={32}
        dropdownLOV={applicationRoles.map((r) => ({
          id: String(r.id),
          name: r.name ?? "",
          isDisabled: firmwareConstraintRoles.some((x) => x.id === r.id),
        }))}
        selectedItem={renderProps.field.value ? [String(renderProps.field.value)] : []}
        onChange={handleRoleSelectChange(renderProps.field)}
        onBlur={renderProps.field.onBlur}
        isDisabled={isLoadingApplicationRoles || isLoadingFirmwareConstraintRoles}
      />
    ),
    [
      applicationRoles,
      firmwareConstraintRoles,
      handleRoleSelectChange,
      isLoadingApplicationRoles,
      isLoadingFirmwareConstraintRoles,
    ]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Role">
        <Controller name="applicationRoleId" control={control} render={renderRoleSelect} />
      </FieldRow>
    </FormPage>
  )
}
